/**
 * Page-level top-bar content. The app `Header` (rendered once in `AppLayout`,
 * outside the routed `<Outlet>`) can't receive props from pages, so pages push
 * their title + an optional badge here and the layout reads it back. Plain
 * primitives (no React nodes) keep the store serializable and cheap.
 *
 * `setPageHeader` merges (zustand's `set` is a shallow merge), so a page can set
 * the title once on mount and update just the badge as data changes.
 *
 * The top bar renders only when a title is set. The inbox sets none — its title
 * sits in its own module header — and reads just the badge, which the
 * conversation list publishes, for the unread pill beside that header.
 */
import { create } from 'zustand';

interface PageHeaderState {
    /** Bold title shown at the top-left of the header; null leaves the top bar empty. */
    readonly title: string | null;
    /** Optional pill next to the title (e.g. "12 Unread"); null hides it. */
    readonly badge: string | null;
    setPageHeader: (next: Partial<Pick<PageHeaderState, 'title' | 'badge'>>) => void;
    clearPageHeader: () => void;
}

export const usePageHeaderStore = create<PageHeaderState>((set) => ({
    title: null,
    badge: null,
    setPageHeader: (next) => set(next),
    clearPageHeader: () => set({ title: null, badge: null }),
}));
